// THE SETUP SURFACE of the new-account welcome — a full-screen first run
// (functions/onboarding.js tells who sees it and what a close decides;
// functions/onboardingWizard.js drives its steps). Nika 2026-10-18: "it should
// be wizard like onboarding and click based onboarding could be possible. this
// will help to personalize the view and avoid showing complex ui at first
// load".
//
// ONE SURFACE, opaque (`document`, the page's own ground): the busy shell is
// never behind a veil, it is simply not on screen until the person has shaped
// it. While a new account's record is read the same surface holds the screen
// with nothing but the workspace's name (`root._onboardingHold`), and the
// wizard opens on it without a flash. Steps: the welcome (Get started, or the
// assistant, or the tour), then click steps on TWO SIDES, never mixed — the
// WORKSPACE side first, the three choices Your work, Your apps and Your idea
// (owners and admins, written for the whole workspace; components/
// OnboardingWorkStep.js, OnboardingStackStep.js, OnboardingIdeaStep.js), then
// the PERSONAL side, this person's own apps, look and home — then done. The
// assistant's conversation draws here too (components/OnboardingChat.js). A
// person who signed up through Fly meets none of those: Fly asked the three
// questions, so the surface holds ONE step for them, "Your workspace is
// ready" (components/OnboardingReadyStep.js, functions/onboardingReady.js). A
// personalized sign-up (POB) meets ONE step too: the build board, a card per
// server step and "Open my workspace" in the foot once every step has an
// outcome (components/OnboardingBuildStep.js, functions/onboardingBuild.js).
//
// THE KIT'S MECHANICS, as AppDialog does them: the root is ALWAYS mounted once
// built; open is a DATA ATTRIBUTE published by a flat factory (`dataOpen`) and
// every state rule is a static `&[data-open]` block (a factory CSS value is
// written inline and beats the `@mobileL` rules; `.isX` blocks apply at create
// only); closed = `visibility: hidden` + `pointer-events: none` after the
// exit. The page behind is inert while the surface is up (`inertOutside`) —
// except while the out-of-credits notice is up: the notice is the layer then,
// and the surface waits under it (see `inertOutside` below).
// Escape is Skip; Tab cycles inside (appDialogTrapTab).
//
// Tiles are buttons with `aria-pressed` (the Personalise panel's own sky tile,
// HomeSkyTile, is the model); surfaces differ by fill, never by a border.

// The hidden watcher — holds the surface for a new account's first load and
// opens the wizard once (functions/onboarding.js onboardingTick). Sibling of
// UsernamePromptGate in shape and mount point: a display:none div whose job is
// a reactive check.
export const OnboardingGate = {
  tag: 'div',
  display: 'none',
  subscribeTo: [
    'signedIn',
    '_authReady',
    'user',
    'userPrefs',
    'workspace',
    'org',
    'currentPath',
    '_wsSwitching',
    '_wpSwitching',
    '_dataLoading',
    '_inviteFlowActive',
    '_usernamePromptPending',
    '_homeCustomizeMode',
    '_canvasSlot',
    '_onboardingDecision',
    '_onboardingFlyArrival',
    '_onboardingTour',
    // the workspace building itself (functions/workspaceSetup.js): the ready
    // screen waits while it holds
    '_workspaceSetup'
  ],
  onCreate: (el) => el.call('onboardingTick'),
  onUpdate: (el) => el.call('onboardingTick'),
  onBeforeRemove: (el) => el.call('onboardingGateStop')
}

export const OnboardingWizard = {
  role: 'dialog',
  ariaModal: 'true',
  ariaLabelledby: 'onboardingWizardTitle',
  // While the record is read, the surface says it is busy.
  ariaBusyDeps: [(el, s) => s.root && s.root._onboardingFlow],
  ariaBusy: (el, s) =>
    s.root && s.root._onboardingHold && !s.root._onboardingFlow ? 'true' : null,
  dataOpenDeps: [(el, s) => s.root && (s.root._onboardingOpen || s.root._onboardingHold)],
  dataOpen: (el, s) => !!(s.root && (s.root._onboardingOpen || s.root._onboardingHold)),
  // THE LAYER, with one yield. The framework stacks modal layers in the order
  // they went live ("the newest open layer defines what is interactive"), so a
  // surface that opens AFTER the out-of-credits notice (components/
  // CreditBlocker.js, a layer too) would become the top layer and put `inert`
  // on the notice: its ✕, Escape and Tab dead. The surface therefore is no layer
  // while the notice is up — on the notice's own predicate (`creditBlockerShown`,
  // the one source of truth the notice paints with, on the layer path: it writes
  // nothing). It waits under the notice, drawn as before and inert like the rest
  // of the page, and is the layer again, with the focus, the moment the notice
  // goes. A surface that is not open asks nothing.
  inertOutside: (el, s) =>
    !!(s.root && (s.root._onboardingOpen || s.root._onboardingHold)) &&
    !el.call('creditBlockerShown', el, s.root, false),

  // Shell-level, above every page layer, in the Modal band.
  position: 'fixed',
  inset: '0',
  zIndex: '2000',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  minWidth: '0',
  theme: 'document',
  color: 'paragraph',
  visibility: 'hidden',
  pointerEvents: 'none',
  opacity: '0',
  transition: 'opacity C defaultBezier, visibility 0s linear C',
  '&[data-open]': {
    visibility: 'visible',
    pointerEvents: 'auto',
    opacity: '1',
    transition: 'opacity C defaultBezier, visibility 0s linear 0s'
  },

  // Escape is Skip (the decision is closeOnboarding's); Tab stays inside.
  onKeydown: (e, el) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      el.call('closeOnboarding', 'close')
      return
    }
    el.call('appDialogTrapTab', e)
  },
  // …and the same two keys when the focus fell to the page body: the boot
  // screen keeps the page inert for ~3 s, every focus() in that time is a
  // no-op, and the surface can open inside it (functions/onboarding.js
  // onboardingDocumentKeydown — the shell's declared document listener, never
  // a raw one).
  onDocumentKeydown: (e, el) => el.call('onboardingDocumentKeydown', e),

  // THE TOP — the workspace's name, and Skip at every step but the last.
  WizardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'A',
    flexShrink: '0',
    minWidth: '0',
    boxSizing: 'border-box',
    padding: 'A C',
    '@mobileL': { padding: 'Z A' },
    AppMicroLabel: {
      color: 'title',
      text: (el, s) => el.call('onboardingWorkspaceName', s.root) || ''
    },
    AppCapsuleBtnGhost: {
      hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
      // nothing to skip on the last step, Fly's ready screen or the build
      // board (the board's way on is its own foot)
      hide: (el, s) => {
        const flow = s.root && s.root._onboardingFlow
        return !flow || flow.step === 'done' || flow.step === 'ready' || flow.step === 'build'
      },
      text: '{{ workspace.onboarding.chat.skipSetup | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('onboardingSkip')
      }
    }
  },

  // THE STAGE — the one scroll area; the step's column sits in its middle.
  WizardStage: {
    flex: '1 1 auto',
    minHeight: '0',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 'C',
    boxSizing: 'border-box',
    padding: 'A C B',
    '@tabletS': { gap: '0', padding: 'A B B' },
    '@mobileL': { padding: 'Z A B' },

    OnboardingStudio: {},

    WizardColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'B',
      width: '100%',
      flex: '1 1 0',
      maxWidth: 'overlayLg',
      minWidth: '0',
      // centred while it is shorter than the stage, scrolling from the top
      // once it is taller (auto margins never clip the start)
      margin: 'auto 0',

      OnboardingJourney: {},

      // The record is still being read: the name and one calm line.
      WizardHold: {
        hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
        hide: (el, s) => !!(s.root && s.root._onboardingFlow),
        tag: 'p',
        margin: '0',
        textAlign: 'center',
        fontSize: 'Z2',
        color: 'inactive',
        text: '{{ workspace.onboarding.wizard.preparing | polyglot }}'
      },

      WizardHead: {
        hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
        hide: (el, s) => !(s.root && s.root._onboardingFlow),
        display: 'flex',
        flexDirection: 'column',
        gap: 'Z',
        minWidth: '0',
        MobileMark: {
          display: 'none',
          '@tabletS': { display: 'flex' },
          hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
          hide: (el, s) =>
            !['welcome', 'done', 'ready', 'build'].includes(s.root?._onboardingFlow?.step),
          alignItems: 'center',
          justifyContent: 'center',
          boxSize: 'avatarLg',
          round: 'radiusCard',
          marginBottom: 'Y',
          theme: 'common-box',
          backgroundImage:
            'linear-gradient(135deg, color-mix(in srgb, var(--color-blue) 20%, transparent), color-mix(in srgb, var(--color-purple) 20%, transparent))',
          ariaHidden: 'true',
          Icon: { name: 'magicstar', boxSize: 'avatarXs', color: 'title' }
        },
        AppMicroLabel: {
          hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
          hide: (el, s) => !(el.call('onboardingWizardAt', s.root) || {}).n,
          textDeps: [(el, s) => s.root && s.root._onboardingFlow],
          text: (el, s) => {
            const at = el.call('onboardingWizardAt', s.root) || {}
            return String(el.call('polyglot', 'workspace.onboarding.wizard.progress') || '')
              .replace('{n}', String(at.n))
              .replace('{total}', String(at.total))
          }
        },
        // The step's name — the dialog's title, an `h2` like the kit's
        // AppDialogTitle (the surface is a modal over the page; it never sits
        // in a pane). It takes the focus when the surface opens (the layer
        // focuses its `[autofocus]` first — the first control would be Skip)
        // and on every step change (onboardingWizardFocus), so a screen
        // reader reads where the person is. A heading is not a control: it
        // wears no focus ring (the kit's ring is a box-shadow on
        // `[tabindex]:focus-visible`).
        WizardTitle: {
          tag: 'h2',
          id: 'onboardingWizardTitle',
          tabindex: '-1',
          autofocus: true,
          margin: '0',
          fontSize: 'E',
          '@mobileL': { fontSize: 'F' },
          fontWeight: '500',
          lineHeight: '1.1',
          color: 'title',
          overflowWrap: 'break-word',
          ':focus': { outline: 'none', boxShadow: 'none' },
          textDeps: [(el, s) => s.root && s.root._onboardingFlow],
          text: (el, s) => {
            const flow = (s.root && s.root._onboardingFlow) || {}
            const KEYS = {
              work: 'workspace.onboarding.wizard.workTitle',
              stack: 'workspace.onboarding.wizard.stackTitle',
              idea: 'workspace.onboarding.wizard.ideaTitle',
              apps: 'workspace.onboarding.wizard.appsTitle',
              look: 'workspace.onboarding.wizard.lookTitle',
              home: 'workspace.onboarding.wizard.homeTitle',
              done: 'workspace.onboarding.wizard.doneTitle',
              ready: 'workspace.onboarding.ready.title',
              assistant: 'workspace.onboarding.chat.title'
            }
            // the build board names the workspace it sets up
            if (flow.step === 'build') {
              return el.call(
                'onboardingText',
                el.call('onboardingWorkspaceName', s.root)
                  ? 'workspace.pobBuild.title'
                  : 'workspace.pobBuild.titleNoName'
              )
            }
            if (KEYS[flow.step]) return el.call('onboardingText', KEYS[flow.step])
            return el.call('onboardingFirstName', s.root)
              ? el.call('onboardingText', 'workspace.onboarding.welcomeTitle')
              : el.call('onboardingText', 'workspace.onboarding.welcomeTitleNoName')
          }
        },
        WizardLead: {
          tag: 'p',
          margin: '0',
          fontSize: 'A',
          '@mobileL': { fontSize: 'Z2' },
          lineHeight: '1.5',
          color: 'inactive',
          overflowWrap: 'break-word',
          textDeps: [(el, s) => s.root && s.root._onboardingFlow],
          text: (el, s) => {
            const flow = (s.root && s.root._onboardingFlow) || {}
            if (flow.step === 'assistant') return el.call('onboardingProgress', s.root) || ''
            // the build board: each card is a step the sign-up really did
            if (flow.step === 'build') return el.call('onboardingText', 'workspace.pobBuild.lead')
            // Fly's ready screen: Fly set up the workspace WITH the person
            if (flow.step === 'ready') {
              return el.call(
                'onboardingText',
                el.call('onboardingWorkspaceName', s.root)
                  ? 'workspace.onboarding.ready.lead'
                  : 'workspace.onboarding.ready.leadNoName'
              )
            }
            // Your apps speaks of the picked preset, or of the person's own
            // apps for Something else
            const custom = ((flow.picks && flow.picks.work) || '') === 'custom'
            const KEYS = {
              work: 'workspace.onboarding.wizard.workLead',
              stack: custom
                ? 'workspace.onboarding.wizard.stackLeadCustom'
                : 'workspace.onboarding.wizard.stackLead',
              idea: 'workspace.onboarding.wizard.ideaLead',
              apps: 'workspace.onboarding.wizard.appsLead',
              look: 'workspace.onboarding.wizard.lookLead',
              home: 'workspace.onboarding.wizard.homeLead',
              done: 'workspace.onboarding.wizard.doneLead'
            }
            return el.call('onboardingText', KEYS[flow.step] || 'workspace.onboarding.welcomeLead')
          }
        }
      },

      OnboardingLesson: {},

      OnboardingWelcomeStep: {},
      OnboardingWorkStep: {},
      OnboardingStackStep: {},
      OnboardingIdeaStep: {},
      OnboardingAppsStep: {},
      OnboardingLookStep: {},
      OnboardingHomeStep: {},
      OnboardingDoneStep: {},
      OnboardingReadyStep: {},
      OnboardingBuildStep: {},
      OnboardingChat: {
        hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
        hide: (el, s) => {
          const flow = s.root && s.root._onboardingFlow
          return !(flow && flow.step === 'assistant')
        }
      }
    }
  },

  // THE FOOT — Back and Continue on the click steps (Skip where a step can be
  // passed by: Your work with no pick, Your idea with no sentence); the
  // conversation's Start working — Build my app with an idea — once its last
  // question is answered. The foot stays on
  // screen (the stage scrolls above it); its row lines up with the step's
  // column, so on a wide screen the buttons sit under the tiles they act on,
  // not in the screen's far corners.
  // On the build board the foot is its way on — "Open my workspace", once
  // every step has an outcome (or the board's poll window passed): a bar that
  // stays on screen while the cards scroll (the phone's sticky bar).
  WizardFoot: {
    hideDeps: [(el, s) => s.root && s.root._onboardingFlow, (el, s) => s.root && s.root._pobBuild],
    hide: (el, s) => {
      const flow = s.root && s.root._onboardingFlow
      if (!flow) return true
      if (flow.step === 'assistant') return false
      if (flow.step === 'build') return !(el.call('onboardingBuildView', s.root) || {}).ctaShown
      return ['work', 'stack', 'idea', 'apps', 'look', 'home'].indexOf(flow.step) === -1
    },
    display: 'flex',
    justifyContent: 'center',
    gap: 'C',
    flexShrink: '0',
    boxSizing: 'border-box',
    padding: 'A C',
    '@tabletS': { gap: '0', padding: 'A B' },
    '@mobileL': { padding: 'Z A' },
    FootSpacer: {
      flex: '1 1 0',
      width: '100%',
      maxWidth: 'overlayMd',
      ariaHidden: 'true',
      '@tabletS': { display: 'none' }
    },
    FootRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 'A',
      width: '100%',
      flex: '1 1 0',
      maxWidth: 'overlayLg',
      minWidth: '0',
      AppCapsuleBtnGhost: {
        // the build board has no step to go back to
        hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
        hide: (el, s) => ((s.root && s.root._onboardingFlow) || {}).step === 'build',
        text: '{{ workspace.onboarding.wizard.back | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('onboardingWizardGo', -1)
        }
      },
      // the build board's line beside its way on: "Your workspace is ready",
      // or that some steps still run (its poll window passed)
      FootReady: {
        tag: 'p',
        role: 'status',
        flex: '1 1 auto',
        minWidth: '0',
        margin: '0',
        fontSize: 'Z2',
        fontWeight: '500',
        lineHeight: '1.4',
        color: 'title',
        overflowWrap: 'break-word',
        hideDeps: [
          (el, s) => s.root && s.root._onboardingFlow,
          (el, s) => s.root && s.root._pobBuild
        ],
        hide: (el, s) =>
          ((s.root && s.root._onboardingFlow) || {}).step !== 'build' ||
          !(el.call('onboardingBuildView', s.root) || {}).footText,
        textDeps: [(el, s) => s.root && s.root._pobBuild, (el, s) => s.root && s.root.lang],
        text: (el, s) => (el.call('onboardingBuildView', s.root) || {}).footText || ''
      },
      AppCapsuleBtnPrimary: {
        hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
        hide: (el, s) => {
          const flow = (s.root && s.root._onboardingFlow) || {}
          // the conversation shows its finish only after its last question
          return flow.step === 'assistant' && !!(flow.ask || !(flow.turns && flow.turns.length))
        },
        textDeps: [
          (el, s) => s.root && s.root._onboardingFlow,
          (el, s) => s.root && s.root._workspacePresets
        ],
        text: (el, s) => {
          const flow = (s.root && s.root._onboardingFlow) || {}
          if (flow.step === 'build') return el.call('polyglot', 'workspace.pobBuild.open')
          if (flow.step === 'assistant') {
            return el.call(
              'polyglot',
              el.call('onboardingIdeaOf', s.root)
                ? 'workspace.onboarding.wizard.buildIdea'
                : 'workspace.onboarding.chat.finish'
            )
          }
          return el.call(
            'polyglot',
            el.call('onboardingFootNext', s.root) || 'workspace.onboarding.wizard.next'
          )
        },
        onClick: (e, el, s) => {
          e.stopPropagation()
          const flow = (s.root && s.root._onboardingFlow) || {}
          // the build board's way on: a plain close (it decides done, via
          // 'pob', because the board settled)
          if (flow.step === 'build') el.call('closeOnboarding', 'close')
          else if (flow.step !== 'assistant') el.call('onboardingWizardGo', 1)
          else if (el.call('onboardingIdeaOf', s.root)) el.call('onboardingWizardFinish', 'build')
          else el.call('closeOnboarding', 'close')
        }
      }
    }
  }
}

// ── The steps ───────────────────────────────────────────────────────────────

// One step's body: shown while the flow stands on `step`.
export const OnboardingWizardStep = {
  wizardStep: '',
  display: 'flex',
  flexDirection: 'column',
  gap: 'B',
  minWidth: '0',
  hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
  hide: (el, s) => {
    const flow = s.root && s.root._onboardingFlow
    return !(flow && flow.step === el.wizardStep)
  }
}

// THE WELCOME — Get started; or the assistant, or the tour. A member reads
// the workspace's language here (THE LAW: the admins choose it).
export const OnboardingWelcomeStep = {
  extends: 'OnboardingWizardStep',
  wizardStep: 'welcome',
  WelcomeStart: {
    display: 'flex',
    minWidth: '0',
    AppCapsuleBtnPrimary: {
      width: '100%',
      padding: 'A B',
      text: '{{ workspace.onboarding.wizard.start | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('onboardingChoose', 'wizard')
      }
    }
  },
  WelcomeWays: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'Y',
    minWidth: '0',
    AppMicroLabel: {
      text: '{{ workspace.onboarding.wizard.otherWays | polyglot }}'
    },
    OnboardingWay_assistant: {
      way: 'assistant',
      icon: 'magicstar',
      heading: 'workspace.onboarding.wayAssistant',
      hint: 'workspace.onboarding.wayAssistantHint'
    },
    OnboardingWay_tour: {
      way: 'tour',
      icon: 'playOval',
      heading: 'workspace.onboarding.wayTour',
      hint: 'workspace.onboarding.wayTourHint'
    }
  },
  WelcomeLanguage: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'inactive',
    text: (el, s) => {
      const lang = el.call('onboardingLanguage', s.root) || {}
      const choice = (lang.choices || []).find((c) => c.value === lang.current)
      return el.call('onboardingText', 'workspace.onboarding.chat.languageSet', {
        language: choice ? choice.label : lang.current || ''
      })
    }
  },
  WelcomeReplay: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    lineHeight: '1.5',
    color: 'inactive',
    text: '{{ workspace.onboarding.replayHint | polyglot }}'
  }
}

// THE APPS — the home shows the picked ones; the rest stay in All apps.
export const OnboardingAppsStep = {
  extends: 'OnboardingWizardStep',
  wizardStep: 'apps',
  AppsBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'Z',
    minWidth: '0',
    AppsCount: {
      tag: 'span',
      fontSize: 'Z1',
      color: 'inactive',
      textDeps: [(el, s) => s.root && s.root._onboardingFlow],
      text: (el, s) => {
        const apps = el.call('onboardingWizardApps', s.root) || []
        return String(el.call('polyglot', 'workspace.onboarding.wizard.appsCount') || '')
          .replace('{n}', String(apps.filter((a) => a.picked).length))
          .replace('{total}', String(apps.length))
      }
    },
    AppsActions: {
      display: 'flex',
      gap: 'Y',
      AppCapsuleBtnGhost_all: {
        extends: 'AppCapsuleBtnGhost',
        text: '{{ workspace.onboarding.wizard.appsAll | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('onboardingWizardAllApps', true)
        }
      },
      AppCapsuleBtnGhost_none: {
        extends: 'AppCapsuleBtnGhost',
        text: '{{ workspace.onboarding.wizard.appsNone | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('onboardingWizardAllApps', false)
        }
      }
    }
  },
  // Rows wide enough for an app's whole name ("Infrastructure", or longer in
  // Georgian): two columns in the column, one on a phone.
  OnboardingTileGrid: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-F2)), 1fr))',
    childExtends: 'OnboardingAppTile',
    childrenDeps: [(el, s) => s.root && s.root._onboardingFlow],
    children: (el, s) => el.call('onboardingWizardApps', s.root) || []
  }
}

// THE LOOK — theme, then background (the Personalise panel's own sky tiles).
export const OnboardingLookStep = {
  extends: 'OnboardingWizardStep',
  wizardStep: 'look',
  OnboardingWizardGroup: {
    caption: 'workspace.personalize.theme',
    OnboardingTileGrid: {
      childExtends: 'OnboardingChoiceTile',
      children: () => [
        {
          key: 'theme-light',
          question: 'theme',
          value: 'light',
          label: 'workspace.personalize.themeLight',
          preview: 'light'
        },
        {
          key: 'theme-dark',
          question: 'theme',
          value: 'dark',
          label: 'workspace.personalize.themeDark',
          preview: 'dark'
        },
        {
          key: 'theme-auto',
          question: 'theme',
          value: 'auto',
          label: 'workspace.personalize.themeAuto',
          hint: 'workspace.onboarding.wizard.themeAutoHint',
          preview: 'auto'
        }
      ]
    }
  },
  OnboardingWizardGroup_1: {
    caption: 'workspace.personalize.background',
    if: (el) => (el.call('homeSkyChoices') || []).some((c) => c && c.value !== 'none'),
    OnboardingTileGrid: {
      childExtends: 'OnboardingWizardSky',
      children: (el) =>
        (el.call('homeSkyChoices') || []).map((c) => ({
          key: 'sky-' + c.key,
          value: c.value,
          label: c.label
        }))
    }
  }
}

// THE HOME — the summary, then where the apps sit.
export const OnboardingHomeStep = {
  extends: 'OnboardingWizardStep',
  wizardStep: 'home',
  OnboardingWizardGroup_preset: {
    caption: 'workspace.onboarding.preset.caption',
    OnboardingTileGrid: {
      childExtends: 'OnboardingPresetTile',
      children: () => [
        {
          key: 'focus',
          value: 'focus',
          summary: 'brief',
          preview: 'brief',
          label: 'workspace.onboarding.preset.focus',
          hint: 'workspace.onboarding.preset.focusHint'
        },
        {
          key: 'overview',
          value: 'overview',
          summary: 'signals',
          preview: 'signals',
          label: 'workspace.onboarding.preset.overview',
          hint: 'workspace.onboarding.preset.overviewHint'
        }
      ]
    }
  },
  OnboardingWizardGroup_1: {
    caption: 'workspace.personalize.appStrip',
    OnboardingTileGrid: {
      childExtends: 'OnboardingChoiceTile',
      children: () => [
        {
          key: 'strip-dock',
          question: 'strip',
          value: 'dock',
          label: 'workspace.onboarding.chat.stripDock',
          hint: 'workspace.onboarding.chat.stripDockHint',
          preview: 'dock'
        },
        {
          key: 'strip-rail',
          question: 'strip',
          value: 'rail',
          label: 'workspace.onboarding.chat.stripRail',
          hint: 'workspace.onboarding.chat.stripRailHint',
          preview: 'rail'
        },
        {
          key: 'strip-off',
          question: 'strip',
          value: 'off',
          label: 'workspace.onboarding.chat.stripOff',
          hint: 'workspace.onboarding.chat.stripOffHint',
          preview: 'off'
        }
      ]
    }
  }
}

// DONE — what the three choices set up (the apply's own lines), the idea if
// there is one, then the way in: Build my app with an idea (the app builder
// opens on it) — Start working beside it — else Start working; or the tour,
// or a first question to the assistant.
export const OnboardingDoneStep = {
  extends: 'OnboardingWizardStep',
  wizardStep: 'done',
  OnboardingSetupLines: {},
  DoneIdea: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'paragraph',
    overflowWrap: 'break-word',
    hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
    hide: (el, s) => !el.call('onboardingIdeaOf', s.root),
    textDeps: [(el, s) => s.root && s.root._onboardingFlow],
    text: (el, s) =>
      String(el.call('polyglot', 'workspace.onboarding.wizard.doneIdea') || '').replace(
        '{idea}',
        el.call('onboardingIdeaOf', s.root) || ''
      )
  },
  OnboardingRecap: {},
  DoneStart: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'Z',
    minWidth: '0',
    AppCapsuleBtnPrimary: {
      width: '100%',
      padding: 'A B',
      textDeps: [(el, s) => s.root && s.root._onboardingFlow],
      text: (el, s) =>
        el.call(
          'polyglot',
          el.call('onboardingIdeaOf', s.root)
            ? 'workspace.onboarding.wizard.buildIdea'
            : 'workspace.onboarding.chat.finish'
        ),
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('onboardingWizardFinish', el.call('onboardingIdeaOf', s.root) ? 'build' : 'home')
      }
    },
    AppCapsuleBtnGhost: {
      width: '100%',
      hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
      hide: (el, s) => !el.call('onboardingIdeaOf', s.root),
      text: '{{ workspace.onboarding.chat.finish | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('onboardingWizardFinish', 'home')
      }
    }
  },
  DoneWays: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'Y',
    minWidth: '0',
    OnboardingWay_tour: {
      icon: 'playOval',
      heading: 'workspace.onboarding.wayTour',
      hint: 'workspace.onboarding.wayTourHint',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('onboardingWizardFinish', 'tour')
      }
    },
    OnboardingWay_assistant: {
      icon: 'magicstar',
      heading: 'workspace.onboarding.chat.askLabel',
      hint: 'workspace.onboarding.tour.composerText',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('onboardingWizardFinish', 'assistant')
      }
    }
  }
}

// ── The parts ───────────────────────────────────────────────────────────────

// A captioned group of tiles inside a step (its caption sits under the
// step's `h2` title).
export const OnboardingWizardGroup = {
  tag: 'section',
  caption: '',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  minWidth: '0',
  GroupCaption: {
    tag: 'h3',
    margin: '0',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    text: (el) => el.call('polyglot', el.parent.caption || '')
  }
}

// The tiles' grid: as many columns as fit, never a tile narrower than E1.
export const OnboardingTileGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-E1)), 1fr))',
  gap: 'Z',
  minWidth: '0',
  childrenAs: 'state'
}

// ONE TILE — a toggle button: its preview, its name, one line about it, and
// a check when it is pressed. The pressed state is the tile's own
// `aria-pressed` (a factory per kind below); the look is declared once here.
// The check is the shell's pick mark (`checkmark`, as in the combobox and the
// menus) — `check` is the Tasks app's own glyph.
export const OnboardingTile = {
  tag: 'button',
  type: 'button',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 'Z',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'A',
  border: 'none',
  round: 'radiusCard',
  theme: 'common-box',
  color: 'title',
  textAlign: 'start',
  cursor: 'pointer',
  transition: 'background-color A defaultBezier, transform Z defaultBezier',
  ':hover': { background: 'hover' },
  ':active': { transform: 'scale(0.98)' },
  '&[aria-pressed="true"]': { background: 'selected' },
  '&[aria-pressed="true"] .onboarding-tile-check': { opacity: '1', transform: 'none' },

  TileCheck: {
    class: 'onboarding-tile-check',
    position: 'absolute',
    top: 'Y',
    right: 'Y',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: 'B',
    round: 'radiusPill',
    theme: 'primary',
    opacity: '0',
    transform: 'scale(0.6)',
    transition: 'opacity A defaultBezier, transform A defaultBezier',
    ariaHidden: 'true',
    Icon: { name: 'checkmark', boxSize: 'iconSm' }
  },

  TileWords: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'W',
    minWidth: '0',
    TileLabel: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      lineHeight: '1.35',
      color: 'title',
      overflowWrap: 'break-word',
      text: (el, s) => el.call('polyglot', (s && s.label) || (s && s.title) || '')
    },
    TileHint: {
      tag: 'span',
      hide: (el, s) => !(s && s.hint),
      fontSize: 'Z',
      lineHeight: '1.45',
      color: 'inactive',
      overflowWrap: 'break-word',
      text: (el, s) => el.call('polyglot', (s && s.hint) || '')
    }
  }
}

// A choice of one question (theme, summary, strip): pressed when it is the
// question's value; a click applies it at once. Its preview draws the choice.
export const OnboardingChoiceTile = {
  extends: 'OnboardingTile',
  ariaPressedDeps: [(el, s) => s.root && s.root._onboardingFlow],
  ariaPressed: (el, s) =>
    s && el.call('onboardingWizardCurrent', s.question) === s.value ? 'true' : 'false',
  // The Rail steps aside on a phone (components/HomeAppRail.js), so a phone
  // is never offered it.
  '@mobileL': { '&[data-preview="rail"]': { display: 'none' } },
  dataPreview: (el, s) => (s && s.preview) || null,
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (s && s.question) el.call('onboardingWizardPick', s.question, s.value)
  },
  OnboardingPreview: { order: '-1' }
}

// An app: its calm glyph (the App strip's own tile), its name and — once it
// is picked — the list's plain tick at the row's end, in the flow (a dozen
// picked rows stay quiet; no badge sits over the name). A click flips the
// pick (written when the step is left forward).
export const OnboardingAppTile = {
  extends: 'OnboardingTile',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 'Z',
  ariaPressedDeps: [(el, s) => s.root && s.root._onboardingFlow],
  ariaPressed: (el, s) => (s && s.picked ? 'true' : 'false'),
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (s && s.id) el.call('onboardingWizardToggleApp', s.id)
  },
  AppGlyphTile: { order: '-1' },
  TileWords: { flex: '1 1 auto' },
  TileCheck: { position: 'static', order: '1', flexShrink: '0', theme: 'transparent' }
}

// A sky — the Personalise panel's own tile (its art, its pressed state); the
// pick goes through the wizard, so it counts as progress.
export const OnboardingWizardSky = {
  extends: 'HomeSkyTile',
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (s && s.value) el.call('onboardingWizardPick', 'sky', s.value)
  }
}

// ── The previews ────────────────────────────────────────────────────────────
// A small drawing of what a choice does, from tokens only: a window in light
// or dark, the summary as two lines or four tiles, the apps as a row, a
// column, or none. `s.preview` names it; each part shows for its own names.

export const OnboardingPreview = {
  position: 'relative',
  display: 'flex',
  boxSizing: 'border-box',
  width: '100%',
  height: 'D',
  overflow: 'hidden',
  round: 'radiusControl',
  theme: 'document',
  ariaHidden: 'true',

  // THEME — a window: a bar on top, two lines; Auto is half each.
  PreviewWindow: {
    if: (el, s) => !!s && ['light', 'dark', 'auto'].indexOf(s.preview) !== -1,
    display: 'flex',
    flex: '1 1 auto',
    minWidth: '0',
    OnboardingPreviewPane: {
      if: (el, s) => s.preview !== 'dark',
      background: 'white',
      Bar: { background: 'black.06' },
      LineA: { background: 'black.12' },
      LineB: { background: 'black.06' }
    },
    OnboardingPreviewPane_1: {
      if: (el, s) => s.preview !== 'light',
      background: 'black',
      Bar: { background: 'white.12' },
      LineA: { background: 'white.18' },
      LineB: { background: 'white.12' }
    }
  },

  // SUMMARY — the Brief's two calm lines, or the Signals' four tiles.
  PreviewBrief: {
    if: (el, s) => !!s && s.preview === 'brief',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 'Y',
    flex: '1 1 auto',
    padding: 'Z A',
    LineA: { height: 'Y', width: '80%', round: 'radiusPill', background: 'line' },
    LineB: { height: 'Y', width: '55%', round: 'radiusPill', background: 'line' }
  },
  PreviewSignals: {
    if: (el, s) => !!s && s.preview === 'signals',
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    alignItems: 'center',
    gap: 'Y',
    flex: '1 1 auto',
    padding: 'Z',
    childExtends: 'OnboardingPreviewCell',
    Cell_1: {},
    Cell_2: {},
    Cell_3: {},
    Cell_4: {}
  },

  // STRIP — the apps in a row above the board, in a column beside it, or
  // not at all.
  PreviewStrip: {
    if: (el, s) => !!s && ['dock', 'rail', 'off'].indexOf(s.preview) !== -1,
    display: 'flex',
    flexDirection: (el, s) => (s.preview === 'rail' ? 'row' : 'column'),
    gap: 'Y',
    flex: '1 1 auto',
    padding: 'Y',
    StripCapsule: {
      if: (el, s) => s.preview !== 'off',
      display: 'flex',
      flexDirection: (el, s) => (s.preview === 'rail' ? 'column' : 'row'),
      alignItems: 'center',
      alignSelf: (el, s) => (s.preview === 'rail' ? 'stretch' : 'center'),
      gap: 'X',
      padding: 'X',
      round: 'radiusPill',
      background: 'selected',
      childExtends: 'OnboardingPreviewDot',
      Dot_1: {},
      Dot_2: {},
      Dot_3: {},
      Dot_4: {}
    },
    StripBoard: {
      flex: '1 1 auto',
      minWidth: '0',
      round: 'radiusControl',
      background: 'line'
    }
  }
}

// One pane of the theme window.
export const OnboardingPreviewPane = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'X',
  flex: '1 1 0',
  minWidth: '0',
  padding: 'Y',
  Bar: { height: 'Y', width: '100%', round: 'radiusPill' },
  LineA: { height: 'X', width: '70%', round: 'radiusPill' },
  LineB: { height: 'X', width: '45%', round: 'radiusPill' }
}

// One tile of the Signals preview.
export const OnboardingPreviewCell = {
  height: 'B',
  round: 'radiusControl',
  background: 'line'
}

// One app of the strip preview.
export const OnboardingPreviewDot = {
  boxSize: 'Y',
  round: 'radiusPill',
  background: 'caption'
}

// One way forward (the welcome's other ways, the done step's ways): its glyph,
// its name, one line about it. The whole row is the button.
export const OnboardingWay = {
  tag: 'button',
  type: 'button',
  way: '',
  icon: '',
  heading: '',
  hint: '',
  display: 'flex',
  alignItems: 'center',
  gap: 'A',
  width: '100%',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'Z1 A',
  border: 'none',
  round: 'radiusCard',
  theme: 'layer',
  color: 'title',
  textAlign: 'start',
  cursor: 'pointer',
  transition: 'background-color A defaultBezier, transform Z defaultBezier',
  ':hover': { background: 'selected' },
  ':active': { background: 'selected', transform: 'scale(0.99)' },
  onClick: (e, el) => {
    e.stopPropagation()
    el.call('onboardingChoose', el.way)
  },

  WayGlyph: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
    boxSize: 'avatarSm',
    round: 'radiusControl',
    theme: 'dialog',
    color: 'title',
    Icon: { name: (el) => el.parent.parent.icon || 'star', boxSize: 'iconMd' }
  },

  WayWords: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'W',
    flex: '1 1 auto',
    minWidth: '0',
    WayName: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      lineHeight: '1.4',
      color: 'title',
      overflowWrap: 'break-word',
      text: (el) => el.call('polyglot', el.parent.parent.heading || '')
    },
    WayHint: {
      tag: 'span',
      fontSize: 'Z',
      lineHeight: '1.45',
      color: 'inactive',
      overflowWrap: 'break-word',
      text: (el) => el.call('polyglot', el.parent.parent.hint || '')
    }
  },

  Icon: {
    name: 'chevronRight',
    boxSize: 'iconSm',
    flexShrink: '0',
    color: 'caption'
  }
}

// The journey is deliberately small: a named sequence, not another menu.
export const OnboardingJourney = {
  tag: 'ol',
  class: 'onboarding-journey',
  ariaLabel: (el) => el.call('polyglot', 'workspace.onboarding.journey.label'),
  hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
  hide: (el, s) =>
    !s.root?._onboardingFlow ||
    ['assistant', 'ready', 'build'].includes(s.root._onboardingFlow.step),
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'Y A',
  padding: '0',
  margin: '0',
  listStyle: 'none',
  childrenAs: 'state',
  childExtends: 'OnboardingJourneyStop',
  childrenDeps: [(el, s) => s.root && s.root._onboardingFlow],
  children: (el, s) => el.call('onboardingWizardJourney', s.root) || []
}

export const OnboardingJourneyStop = {
  tag: 'li',
  display: 'flex',
  alignItems: 'center',
  gap: 'Y',
  fontSize: 'Z',
  color: 'caption',
  ariaCurrent: (el, s) => (s.status === 'current' ? 'step' : null),
  '&[aria-current="step"]': { color: 'title', fontWeight: '700' },
  // the side this stop opens — "For the workspace" before the three choices,
  // "For you" before the person's own steps (onboardingWizardJourney)
  JourneySide: {
    tag: 'span',
    hide: (el, s) => !s.sideLabel,
    fontSize: 'Z',
    fontWeight: '500',
    color: 'title',
    text: (el, s) => (s.sideLabel ? el.call('polyglot', s.sideLabel) : '')
  },
  Number: {
    tag: 'span',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: 'avatarXs',
    round: 'radiusPill',
    theme: (el, s) => (s.status === 'current' ? 'primary' : 'layer'),
    text: (el, s) => (s.status === 'complete' ? '' : String(s.number)),
    Icon: { if: (el, s) => s.status === 'complete', name: 'checkmark', boxSize: 'iconXs' }
  },
  Text: { text: (el, s) => el.call('polyglot', s.label) }
}

// Useful guidance stays in the reading column even when the companion
// preview steps away on smaller screens. No extra tutorial to dismiss. Not on
// the workspace side's three choices, nor on Fly's ready screen or the build
// board: their lead is their one line (say it once — onboardingWizardLesson).
export const OnboardingLesson = {
  hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
  hide: (el, s) => {
    const step = s.root?._onboardingFlow?.step
    return (
      !step ||
      step === 'work' ||
      step === 'stack' ||
      step === 'idea' ||
      step === 'ready' ||
      step === 'build'
    )
  },
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'Z',
  minWidth: '0',
  Icon: {
    boxSize: 'iconMd',
    flexShrink: '0',
    color: 'caption',
    nameDeps: [(el, s) => s.root && s.root._onboardingFlow],
    name: (el, s) => el.call('onboardingWizardLesson', s.root).icon
  },
  Text: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z1',
    lineHeight: '1.5',
    color: 'caption',
    textDeps: [(el, s) => s.root && s.root._onboardingFlow],
    text: (el, s) => {
      const key = el.call('onboardingWizardLesson', s.root).text
      return key ? el.call('polyglot', key) : ''
    }
  }
}

export const OnboardingPresetTile = {
  extends: 'OnboardingTile',
  ariaPressedDeps: [(el, s) => s.root && s.root._onboardingFlow],
  ariaPressed: (el, s) =>
    el.call('onboardingWizardCurrent', 'summary') === s.summary &&
    el.call('onboardingWizardCurrent', 'strip') === 'dock'
      ? 'true'
      : 'false',
  onClick: (e, el, s) => {
    e.stopPropagation()
    el.call('onboardingWizardPreset', s.value)
  },
  OnboardingPreview: { order: '-1' }
}

// Real current settings, so finishing a replay describes what is kept too.
export const OnboardingRecap = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'Y',
  childrenAs: 'state',
  childExtends: 'OnboardingRecapItem',
  childrenDeps: [(el, s) => s.root && s.root._onboardingFlow],
  children: (el) => {
    const theme = el.call('onboardingWizardCurrent', 'theme')
    const summary = el.call('onboardingWizardCurrent', 'summary')
    const strip = el.call('onboardingWizardCurrent', 'strip')
    return [
      {
        key: 'theme',
        label:
          theme === 'light'
            ? 'workspace.personalize.themeLight'
            : theme === 'dark'
              ? 'workspace.personalize.themeDark'
              : 'workspace.personalize.themeAuto'
      },
      {
        key: 'summary',
        label:
          summary === 'signals'
            ? 'workspace.personalize.summarySignals'
            : 'workspace.personalize.summaryBrief'
      },
      {
        key: 'strip',
        label:
          strip === 'off'
            ? 'workspace.onboarding.chat.stripOff'
            : strip === 'rail'
              ? 'workspace.onboarding.chat.stripRail'
              : 'workspace.onboarding.chat.stripDock'
      }
    ]
  }
}

export const OnboardingRecapItem = {
  display: 'flex',
  alignItems: 'center',
  gap: 'Y',
  padding: 'Y Z',
  round: 'radiusPill',
  theme: 'layer',
  color: 'title',
  fontSize: 'Z1',
  Icon: { name: 'checkmark', boxSize: 'iconXs' },
  Text: { text: (el, s) => el.call('polyglot', s.label) }
}

// A lightweight, declarative preview. It follows real choices without
// mounting a second dashboard, any widgets, canvases, or animation loops. Not
// beside the build board: the board's cards are the real workspace already,
// one column, full width.
export const OnboardingStudio = {
  hideDeps: [(el, s) => s.root && s.root._onboardingFlow],
  hide: (el, s) => !s.root?._onboardingFlow || s.root._onboardingFlow.step === 'build',
  class: 'onboarding-studio',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 'B',
  flex: '1 1 0',
  width: '100%',
  maxWidth: 'overlayMd',
  minWidth: '0',
  margin: 'auto 0',
  padding: 'B',
  boxSizing: 'border-box',
  round: 'radiusSheet',
  theme: 'common-box',
  position: 'relative',
  overflow: 'hidden',
  '@tabletS': { display: 'none' },
  Aura: {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    ariaHidden: 'true',
    backgroundImage:
      'radial-gradient(ellipse at 10% 0%, color-mix(in srgb, var(--color-blue) 24%, transparent), transparent 65%), radial-gradient(ellipse at 100% 40%, color-mix(in srgb, var(--color-purple) 24%, transparent), transparent 65%)'
  },
  Intro: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 'Z',
    AppMicroLabel: { text: '{{ workspace.onboarding.studio.eyebrow | polyglot }}', color: 'title' },
    Title: {
      text: '{{ workspace.onboarding.studio.title | polyglot }}',
      whiteSpace: 'pre-line',
      fontSize: 'E',
      fontWeight: '500',
      lineHeight: '1.1',
      color: 'title',
      maxWidth: '100%'
    }
  },
  Preview: {
    position: 'relative',
    class: 'onboarding-live-preview',
    ariaHidden: 'true',
    display: 'flex',
    flexDirection: 'column',
    gap: 'A',
    padding: 'A',
    round: 'radiusCard',
    theme: 'document',
    minWidth: '0',
    dataSummaryDeps: [(el, s) => s.root && s.root._onboardingFlow],
    dataSummary: (el) => el.call('onboardingWizardCurrent', 'summary'),
    dataStripDeps: [(el, s) => s.root && s.root._onboardingFlow],
    dataStrip: (el) => el.call('onboardingWizardCurrent', 'strip'),
    '&[data-summary="signals"] .onboarding-preview-brief': { display: 'none' },
    '&[data-summary="brief"] .onboarding-preview-signals': { display: 'none' },
    '&[data-strip="off"] .onboarding-preview-apps': { display: 'none' },
    '&[data-strip="rail"] .onboarding-preview-board': { flexDirection: 'row' },
    '&[data-strip="rail"] .onboarding-preview-apps': { flexDirection: 'column' },
    Header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 'Z',
      Text: {
        text: (el, s) => el.call('onboardingWorkspaceName', s.root),
        fontSize: 'Z',
        color: 'caption',
        overflowWrap: 'anywhere'
      },
      Icon: { name: 'search', boxSize: 'iconSm', color: 'caption', flexShrink: '0' }
    },
    Greeting: {
      text: (el, s) =>
        el.call(
          'onboardingText',
          el.call('onboardingFirstName', s.root)
            ? 'workspace.onboarding.welcomeTitle'
            : 'workspace.onboarding.welcomeTitleNoName'
        ),
      fontSize: 'A',
      fontWeight: '500',
      color: 'title'
    },
    Brief: {
      class: 'onboarding-preview-brief',
      display: 'flex',
      flexDirection: 'column',
      gap: 'Y',
      OnboardingPreviewLine: { width: '90%' },
      OnboardingPreviewLine_1: { width: '65%' }
    },
    Signals: {
      class: 'onboarding-preview-signals',
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: 'Y',
      OnboardingPreviewCell: {},
      OnboardingPreviewCell_1: {},
      OnboardingPreviewCell_2: {},
      OnboardingPreviewCell_3: {}
    },
    Board: {
      class: 'onboarding-preview-board',
      display: 'flex',
      flexDirection: 'column',
      gap: 'Z',
      minWidth: '0',
      Apps: {
        class: 'onboarding-preview-apps',
        display: 'flex',
        gap: 'Y',
        minWidth: '0',
        childrenAs: 'state',
        childExtends: 'OnboardingPreviewApp',
        childrenDeps: [(el, s) => s.root && s.root._onboardingFlow],
        children: (el, s) =>
          (el.call('onboardingWizardApps', s.root) || []).filter((a) => a.picked).slice(0, 4)
      },
      Cards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 'Y',
        flex: '1 1 auto',
        minWidth: '0',
        OnboardingPreviewCard: {},
        OnboardingPreviewCard_1: {}
      }
    },
    Prompt: {
      display: 'flex',
      alignItems: 'center',
      gap: 'Y',
      theme: 'common-box',
      padding: 'Z',
      round: 'radiusPill',
      Icon: { name: 'magicstar', boxSize: 'iconSm', color: 'caption' },
      Text: {
        text: '{{ workspace.onboarding.studio.prompt | polyglot }}',
        fontSize: 'Z',
        color: 'caption'
      }
    }
  },
  Caption: {
    tag: 'div',
    textAlign: 'start',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 'Y',
    Title: {
      fontSize: 'A',
      fontWeight: '500',
      color: 'title',
      textDeps: [(el, s) => s.root && s.root._onboardingFlow],
      text: (el, s) => el.call('polyglot', el.call('onboardingWizardLesson', s.root).title)
    },
    Text: {
      text: '{{ workspace.onboarding.studio.caption | polyglot }}',
      fontSize: 'Z1',
      lineHeight: '1.5',
      color: 'caption'
    }
  }
}

export const OnboardingPreviewLine = {
  height: 'Y',
  width: '75%',
  round: 'radiusPill',
  background: 'line'
}

export const OnboardingPreviewCard = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  padding: 'Z',
  round: 'radiusControl',
  theme: 'common-box',
  OnboardingPreviewLine: { width: '55%' },
  OnboardingPreviewLine_1: {},
  OnboardingPreviewLine_2: { width: '90%' }
}

export const OnboardingPreviewApp = {
  extends: 'AppGlyphTile',
  boxSize: 'avatarSm',
  minWidth: 'avatarSm'
}
